/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Row, Col, Select, Button, Form, InputNumber, Space, Table, Alert, Modal, Typography, Spin, Input } from 'antd';
import { useSelector } from 'react-redux';
import { DataService } from '../../../../config/dataService/smipDataService';
import { handleApiError } from '../../../../utility/errorHandler';
import { processInvestorTransfer } from '../../../../services/manualTransferService';
import CustomTag from '../../../../components/CustomTag';
import { PageHeader } from '../../../../components/page-headers/page-headers';
import { Main } from '../../../styled';
import { Cards } from '../../../../components/cards/frame/cards-frame';

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

function DisburseToInvestor() {
  const { facilitatorId } = useSelector((state) => state.auth);
  const { userType } = useSelector((state) => state.auth);
  const lowerCaseUserType = userType ? userType.toLowerCase() : '';

  const [form] = Form.useForm();
  const [borrowers, setBorrowers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [borrowersLoading, setBorrowersLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [investmentsLoading, setInvestmentsLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [investorDetailsLoading, setInvestorDetailsLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  const [selectedBorrower, setSelectedBorrower] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [investorDetails, setInvestorDetails] = useState(null);
  const [hasWallet, setHasWallet] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [transferResult, setTransferResult] = useState({ success: null, error: null });

  // Fetch all borrowers for the facilitator
  const fetchBorrowers = async () => {
    setBorrowersLoading(true);
    try {
      const response = await DataService.get(`/Borrowers?filter[facilitator_id][_eq]=${facilitatorId}`);
      setBorrowers(response.data || []);
    } catch (error) {
      handleApiError(error);
    } finally {
      setBorrowersLoading(false);
    }
  };

  // Fetch borrower wallet details
  const fetchBorrowerWallet = async (borrowerId) => {
    setWalletLoading(true);
    try {
      const walletResponse = await DataService.get(`/Wallets?filter[borrower_id][_eq]=${borrowerId}`);
      if (walletResponse.data && walletResponse.data.length > 0) {
        setHasWallet(true);
        setWalletData(walletResponse.data[0]);
      } else {
        setHasWallet(false);
        setWalletData(null);
      }
    } catch (error) {
      handleApiError(error);
      setHasWallet(false);
      setWalletData(null);
    } finally {
      setWalletLoading(false);
    }
  };

  // Fetch projects for selected borrower
  const fetchProjects = async (borrowerId) => {
    if (!borrowerId) return;
    setProjectsLoading(true);
    try {
      const response = await DataService.get(
        `/Projects?filter[borower_id][_eq]=${borrowerId}&filter[status][_neq]=completed`,
      );
      setProjects(response.data || []);

      // Fetch borrower wallet details
      await fetchBorrowerWallet(borrowerId);
    } catch (error) {
      handleApiError(error);
    } finally {
      setProjectsLoading(false);
    }
  };

  // Fetch investments for selected project
  const fetchInvestments = async (projectId) => {
    if (!projectId) return;
    setInvestmentsLoading(true);
    try {
      const response = await DataService.get(
        `/Investments?filter[project][_eq]=${projectId}&filter[status][_eq]=active_and_funded&filter[investment_type][_eq]=loan`,
      );
      setInvestments(response.data || []);
    } catch (error) {
      handleApiError(error);
    } finally {
      setInvestmentsLoading(false);
    }
  };

  // Fetch investor details
  const fetchInvestorDetails = async (investorId) => {
    if (!investorId) return null;
    setInvestorDetailsLoading(true);
    try {
      const response = await DataService.get(`/Investors?filter[id][_eq]=${investorId}`);
      const investorData = response.data[0] || null;
      setInvestorDetails(investorData);
      return investorData;
    } catch (error) {
      handleApiError(error);
      return null;
    } finally {
      setInvestorDetailsLoading(false);
    }
  };

  // Handle borrower selection
  const handleBorrowerChange = async (borrowerId) => {
    const borrower = borrowers.find((b) => b.id === borrowerId);
    setSelectedBorrower(borrower);
    setSelectedProject(null);
    setProjects([]);
    setInvestments([]);

    // Only fetch projects after selecting a borrower
    await fetchProjects(borrowerId);
  };

  // Handle project selection
  const handleProjectChange = async (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    setSelectedProject(project);
    setInvestments([]);

    // Only fetch investments after selecting a project
    await fetchInvestments(projectId);
  };

  // Handle opening the disbursement modal
  const showDisbursementModal = async (investment) => {
    setSelectedInvestment(investment);

    // Fetch investor details
    const investorData = await fetchInvestorDetails(investment.investor);

    // Calculate remaining amount to be paid
    const remainingAmount = (investment.expected_amount || 0) - (investment.amount_paid || 0);
    form.setFieldsValue({
      amount: investment.periodic_payment_amount || remainingAmount,
      currency: 'KES',
      userId: investment.investor,
      projectId: investment.project,
      investmentId: investment.id,
      borrowerId: selectedBorrower.id,
      description: `Loan repayment for project: ${selectedProject?.project_title || investment.project}`,
    });

    setIsModalVisible(true);
  };

  // Process the transfer
  const handleDisbursement = async (values) => {
    setTransferLoading(true);
    setTransferResult({ success: null, error: null });

    try {
      // Generate a transfer reference
      const transferReference = `MANUAL-INV-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

      const transferData = {
        ...values,
        transferReference,
      };

      const { data, error } = await processInvestorTransfer(transferData);

      if (error) {
        throw new Error(error);
      }

      // Update investment amount_paid in database
      await DataService.patch(`/Investments/${selectedInvestment.id}`, {
        amount_paid: (selectedInvestment.amount_paid || 0) + Number(values.amount),
      });

      setTransferResult({
        success: `Successfully disbursed $${values.amount} to investor.`,
        error: null,
      });

      // Refresh investments list
      fetchInvestments(selectedProject.id);
      fetchBorrowerWallet(selectedBorrower.id);

      // Close modal after 3 seconds on success
      setTimeout(() => {
        setIsModalVisible(false);
        form.resetFields();
      }, 2500);
    } catch (error) {
      setTransferResult({
        success: null,
        error: `Failed to process disbursement: ${error.message}`,
      });
    } finally {
      setTransferLoading(false);
    }
  };

  // Handle modal cancel
  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setSelectedInvestment(null);
    setTransferResult({ success: null, error: null });
  };

  // Columns for investments table
  const investmentColumns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <CustomTag color="green">{status.replace(/_/g, ' ').toUpperCase()}</CustomTag>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `$${(amount || 0).toLocaleString()}`,
    },
    {
      title: 'Expected Return',
      dataIndex: 'expected_amount',
      key: 'expected_amount',
      render: (amount) => `$${(amount || 0).toLocaleString()}`,
    },
    {
      title: 'Amount Paid',
      dataIndex: 'amount_paid',
      key: 'amount_paid',
      render: (paid, record) => {
        const paidAmount = paid || 0;
        const expectedAmount = record.expected_amount || 0;
        const percentPaid = expectedAmount > 0 ? (paidAmount / expectedAmount) * 100 : 0;

        return (
          <div>
            <div>{`$${paidAmount.toLocaleString()} / $${expectedAmount.toLocaleString()}`}</div>
            <div style={{ fontSize: '0.8rem', color: percentPaid === 100 ? 'green' : 'orange' }}>
              {`${Math.round(percentPaid)}% paid`}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Payment Terms',
      key: 'payment_terms',
      render: (_, record) => (
        <div>
          <div>{`${record.payment_frequency || 'N/A'} payments`}</div>
          <div>{`${record.payment_period || 'N/A'} months`}</div>
          <div>{`$${(record.periodic_payment_amount || 0).toLocaleString()} per payment`}</div>
        </div>
      ),
    },
    {
      title: 'Interest Rate',
      dataIndex: 'interest_rate',
      key: 'interest_rate',
      render: (rate) => `${rate || 0}%`,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const remainingAmount = (record.expected_amount || 0) - (record.amount_paid || 0);
        const isFullyPaid = remainingAmount <= 0;

        return (
          <Button type="primary" onClick={() => showDisbursementModal(record)} disabled={isFullyPaid}>
            {isFullyPaid ? 'Fully Paid' : 'Disburse'}
          </Button>
        );
      },
    },
  ];

  const PageRoutes = [
    {
      path: 'index',
      breadcrumbName: 'Dashboard',
    },
    {
      path: `/${lowerCaseUserType}/disbursements`,
      breadcrumbName: 'Disbursements',
    },
    {
      path: `/${lowerCaseUserType}/disbursements/investor`,
      breadcrumbName: 'Disburse to Investor',
    },
  ];

  return (
    <>
      <PageHeader className="ninjadash-page-header-main" title="Disburse to Investor" routes={PageRoutes} />
      <Main>
        <Row gutter={[15, 15]}>
          <Col xs={24}>
            <Cards title="Selection Panel">
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item label="Select Borrower">
                    <Select
                      placeholder="Select a borrower"
                      onChange={handleBorrowerChange}
                      style={{ width: '100%' }}
                      loading={borrowersLoading}
                      onClick={() => !borrowers.length && fetchBorrowers()}
                    >
                      {borrowers.map((borrower) => (
                        <Option key={borrower.id} value={borrower.id}>
                          {borrower.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item label="Select Project">
                    <Select
                      placeholder="Select a project"
                      onChange={handleProjectChange}
                      style={{ width: '100%' }}
                      loading={projectsLoading}
                      disabled={!selectedBorrower}
                      value={selectedProject?.id}
                    >
                      {projects.map((project) => (
                        <Option key={project.id} value={project.id}>
                          {project.project_title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <div style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                    <Button
                      type="primary"
                      onClick={() => {
                        if (selectedBorrower) fetchProjects(selectedBorrower.id);
                        if (selectedProject) fetchInvestments(selectedProject.id);
                      }}
                      style={{ marginBottom: '5px' }}
                    >
                      Refresh Data
                    </Button>
                  </div>
                </Col>
              </Row>

              {/* Wallet Information */}
              {walletLoading ? (
                <div style={{ textAlign: 'center', padding: '10px' }}>
                  <Spin size="small" />
                  <span style={{ marginLeft: '10px' }}>Loading wallet information...</span>
                </div>
              ) : hasWallet && walletData ? (
                <Alert
                  message="Wallet Information"
                  description={
                    <div>
                      <p>Available Balance: ${(walletData.balance || 0).toLocaleString()}</p>
                      {(walletData.balance || 0) < 100 && (
                        <p style={{ color: 'red' }}>
                          Low balance! You may need to add funds to the wallet before disbursement.
                        </p>
                      )}
                    </div>
                  }
                  type={(walletData.balance || 0) < 100 ? 'warning' : 'info'}
                  showIcon
                  style={{ marginTop: '15px' }}
                />
              ) : selectedBorrower ? (
                <Alert
                  message="No Wallet Found"
                  description="This borrower does not have a wallet set up. Please set up a wallet before proceeding with disbursements."
                  type="warning"
                  showIcon
                  style={{ marginTop: '15px' }}
                />
              ) : null}
            </Cards>
          </Col>
        </Row>

        {selectedProject && (
          <Row gutter={[15, 15]}>
            <Col xs={24}>
              <Cards title={`Loan Investments for Project: ${selectedProject.project_title}`}>
                {investmentsLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: '15px' }}>Loading investments...</div>
                  </div>
                ) : investments.length > 0 ? (
                  <Table
                    columns={investmentColumns}
                    dataSource={investments}
                    rowKey="id"
                    pagination={{ pageSize: 5 }}
                  />
                ) : (
                  <Alert
                    message="No Eligible Investments"
                    description="There are no active and funded loan investments for this project."
                    type="info"
                    showIcon
                  />
                )}
              </Cards>
            </Col>
          </Row>
        )}
      </Main>

      {/* Disbursement Modal */}
      <Modal
        title="Disburse Funds to Investor"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
      >
        {investorDetailsLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '15px' }}>Loading investor details...</div>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleDisbursement}>
            <Form.Item
              name="amount"
              label="Amount to Disburse (KES)"
              rules={[{ required: true, message: 'Please enter amount to disburse' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                min={1}
                placeholder="Enter amount"
              />
            </Form.Item>

            <Form.Item name="borrowerId" label="Borrower ID" hidden>
              <Input />
            </Form.Item>

            <Form.Item name="currency" label="Currency" hidden>
              <Select defaultValue="KES">
                <Option value="KES">KES</Option>
              </Select>
            </Form.Item>

            <Form.Item name="userId" label="Investor ID" hidden>
              <Input />
            </Form.Item>

            <Form.Item name="projectId" label="Project ID" hidden>
              <Input />
            </Form.Item>

            <Form.Item name="investmentId" label="Investment ID" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please enter a description' }]}
            >
              <TextArea rows={3} placeholder="Payment description..." />
            </Form.Item>

            {/* Investment Details */}
            {selectedInvestment && (
              <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '10px', borderRadius: '5px' }}>
                <Title level={5}>Investment Details</Title>
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <Text strong>Investment Amount:</Text>
                    <div>${selectedInvestment.amount}</div>
                  </Col>
                  <Col span={12}>
                    <Text strong>Expected Return:</Text>
                    <div>${selectedInvestment.expected_amount}</div>
                  </Col>
                  <Col span={12}>
                    <Text strong>Already Paid:</Text>
                    <div>${selectedInvestment.amount_paid || 0}</div>
                  </Col>
                  <Col span={12}>
                    <Text strong>Remaining:</Text>
                    <div>${(selectedInvestment.expected_amount || 0) - (selectedInvestment.amount_paid || 0)}</div>
                  </Col>
                </Row>
              </div>
            )}

            {/* Display Investor Info */}
            {investorDetails && (
              <div style={{ marginBottom: '16px' }}>
                <p>
                  <strong>Disbursing to:</strong> {investorDetails.first_name}{' '}
                  {investorDetails.last_name || investorDetails.name}
                </p>
              </div>
            )}

            {/* Wallet Balance Warning */}
            {hasWallet && walletData && (walletData.balance || 0) < (form.getFieldValue('amount') || 0) && (
              <Alert
                message="Insufficient Wallet Balance"
                description={`Your wallet balance ($${walletData.balance || 0}) is less than the amount you're trying to disburse. Please add funds to your wallet before proceeding.`}
                type="warning"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            {/* Error/Success Messages */}
            {transferResult.error && (
              <Alert
                message="Error"
                description={transferResult.error}
                type="error"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            {transferResult.success && (
              <Alert
                message="Success"
                description={transferResult.success}
                type="success"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
              <Space>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={transferLoading}
                  disabled={hasWallet && walletData && (walletData.balance || 0) < (form.getFieldValue('amount') || 0)}
                >
                  Disburse Funds
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
}

export default DisburseToInvestor;
